import { PaginationReturn } from '@common/types/service';
import { Roles } from '@common/types/user';
import { CompanyHeader } from '@company/components/display/headers';
import {
    useDeleteCompanyOfUserMutation,
    useGetManagementUserCompaniesQuery,
    useGetUserCompaniesQuery,
} from '@company/services/company';
import { CompaniesHeader, ICompany } from '@company/types/company';
import { COMPANY_OVERVIEW_TABLE_HEADERS_DASHBOARD_USER, SERVICES_ICONS } from '@company/utils/company';
import { CancelDeleteDialog } from '@components/display/dialogs/CancelDeleteDialog';
import { LoadingContainer } from '@components/display/loading/LoadingContainer';
import { Table } from '@components/display/tables';
import { Filters } from '@components/utility/filters';
import { PUBLIC_PADDING_CLASSNAME } from '@constants/navigation';
import { useAppDispatch, useAppSelector } from '@hooks/generic';
import { useDebounce } from '@hooks/useDebounce';
import { TableCell, Tooltip } from '@mui/material';
import { setTitle } from '@store/appSlice';
import { selectUser } from '@store/userSlice';
import { capitalizeFirstLetter, handleError, toaster } from '@utils/common';
import clsx from 'clsx';
import { createElement, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

interface DialogInfo {
    title: string;
    primary: {
        label: string;
        onClick: () => void;
    };
    secondary: {
        label: string;
        onClick: () => void;
    };
}

const allowedColumns: (keyof CompaniesHeader)[] = ['name', 'country'];

const PAGE_SIZE = 15;

const CompaniesList = () => {
    const user = useAppSelector(selectUser);
    const dispatch = useAppDispatch();
    const [searchParams, setSearchParams] = useSearchParams();

    const filters = useMemo(
        () => ({
            sort: allowedColumns.includes(searchParams.get('sort')?.replace('-', '') as keyof CompaniesHeader)
                ? (searchParams.get('sort') as keyof CompaniesHeader)
                : 'name',
            page: Number.isNaN(searchParams.get('page')) || !searchParams.get('page') ? '1' : searchParams.get('page'),
            search: searchParams.get('search'),
            companyType: Number.isNaN(searchParams.get('company_type')) ? '' : searchParams.get('company_type'),
        }),
        [searchParams]
    );
    const [search, setSearch] = useState(filters.search);
    const isDashboard = useMemo(() => window.location.pathname.includes('/dashboard/'), []);
    const isAdmin = useMemo(() => !!user && user.role === Roles.VICTRON, [user]);

    const handleParamChange = useCallback(
        (field: string, value: string | null) => {
            if (searchParams.get(field) !== value) {
                setSearchParams((prev) => {
                    const params = new URLSearchParams(prev);
                    params.set('page', '1');
                    if (value) {
                        params.set(field, value);
                    } else {
                        params.delete(field);
                    }
                    return params;
                });
            }
        },
        [searchParams, setSearchParams]
    );
    const handleSearchParamChange = useCallback(
        (value: string | null) => {
            handleParamChange('search', value);
        },
        [handleParamChange]
    );

    useDebounce({ value: search, delay: 500, customHandler: handleSearchParamChange });

    useEffect(() => {
        setSearch(filters.search);
    }, [filters.search, setSearchParams]);

    const sort = useMemo(
        () => ({
            column: filters.sort?.replace('-', '') as keyof CompaniesHeader,
            direction: filters.sort?.includes('-') ? 'desc' : ('asc' as 'desc' | 'asc'),
        }),
        [filters.sort]
    );

    const [deleteSelectedCompany, { isLoading: isDeleteLoading }] = useDeleteCompanyOfUserMutation();

    const {
        data: publicUserCompaniesResponse,
        isLoading: isLoadingPublicUserCompanies,
        isFetching: isPublicUserCompaniesFetching,
        error: publicUserCompaniesError,
    } = useGetUserCompaniesQuery(
        {
            filters: {
                search_name: filters.search ? [filters.search] : [],
            },
            ordering: filters.sort || '',
            page: Number(filters.page) || 1,
            page_size: PAGE_SIZE,
        },
        {
            skip: isDashboard,
        }
    );

    const {
        data: managementUserCompaniesResponse,
        isLoading: isLoadingManagementUserCompanies,
        isFetching: isManagementUserCompaniesFetching,
        error: managementUserCompaniesError,
    } = useGetManagementUserCompaniesQuery(
        {
            filters: {
                search_name: filters.search ? [filters.search] : [],
            },
            ordering: filters.sort || '',
            page: Number(filters.page) || 1,
            page_size: PAGE_SIZE,
        },
        {
            skip: !isDashboard,
        }
    );
    const userCompanies = useMemo(
        () =>
            (managementUserCompaniesResponse || publicUserCompaniesResponse) as PaginationReturn<ICompany> | undefined,
        [managementUserCompaniesResponse, publicUserCompaniesResponse]
    );

    const [isDialogOpen, setIsDialogOpen] = useState(false);

    const [dialogInfo, setDialogInfo] = useState<DialogInfo | null>(null);

    const handleSortClick = useCallback(
        (column: keyof CompaniesHeader) => {
            const isDesc = sort.column === column && sort.direction === 'desc';
            setSearchParams((prev) => {
                const params = new URLSearchParams(prev);
                params.set('sort', `${!isDesc ? '-' : ''}${column}`);
                params.set('page', '1');
                return params;
            });
        },
        [sort.column, sort.direction, setSearchParams]
    );

    const selectedActionsRowIdRef = useRef<string | null>(null);

    const setSelectedActionsRowId = (id: string | null) => {
        selectedActionsRowIdRef.current = id;
    };

    const handleDialogClose = useCallback(() => {
        setIsDialogOpen(false);
    }, []);

    const handlePageChange = useCallback(
        (newPage: number) => {
            setSearchParams((prev) => {
                const params = new URLSearchParams(prev);
                params.set('page', `${newPage}`);
                return params;
            });
        },
        [setSearchParams]
    );

    const renderRow = useCallback(
        (company: Partial<ICompany>) => (
            <>
                <TableCell className="pl-30">{company.name}</TableCell>
                <TableCell>
                    {company.services?.map((service) => (
                        <Tooltip title={capitalizeFirstLetter(service.name.split('_').join(' '))} className="w-min">
                            <div>{createElement(SERVICES_ICONS[service.name].icon)}</div>
                        </Tooltip>
                    ))}
                </TableCell>
                <TableCell>{company.country?.name}</TableCell>
            </>
        ),
        []
    );

    const handleRemoveCompany = useCallback(async () => {
        const companyName = userCompanies?.results.find(
            (company) => company.id === selectedActionsRowIdRef.current
        )?.name;
        try {
            if (selectedActionsRowIdRef.current) {
                await deleteSelectedCompany({
                    company_id: selectedActionsRowIdRef.current as string,
                }).unwrap();
                toaster.success({ msg: `${companyName} has been removed` });
                setIsDialogOpen(false);
                if (Number(userCompanies?.results.length) <= 1) {
                    const currentPage = Number(filters.page);
                    handlePageChange((currentPage <= 1 ? 1 : currentPage) - 1);
                }
            }
        } catch (err) {
            handleError('Error removing event', err);
        }
    }, [deleteSelectedCompany, filters.page, handlePageChange, userCompanies?.results]);

    const handleSetDialogRemoveCompany = useCallback(() => {
        const companyName = userCompanies?.results.find(
            (company) => company.id === selectedActionsRowIdRef.current
        )?.name;
        setIsDialogOpen(true);
        setDialogInfo({
            title: `Are you sure you want to remove ${companyName}?`,
            primary: {
                label: 'Remove',
                onClick: handleRemoveCompany,
            },
            secondary: {
                label: 'Go back',
                onClick: handleDialogClose,
            },
        });
    }, [handleDialogClose, handleRemoveCompany, userCompanies]);

    const isLoading = useMemo(
        () => isLoadingManagementUserCompanies || isLoadingPublicUserCompanies,
        [isLoadingManagementUserCompanies, isLoadingPublicUserCompanies]
    );
    const isFetching = useMemo(
        () => isManagementUserCompaniesFetching || isPublicUserCompaniesFetching,
        [isManagementUserCompaniesFetching, isPublicUserCompaniesFetching]
    );

    const rowOptions = useMemo(
        () =>
            isDashboard && isAdmin
                ? [
                      {
                          label: 'Remove',
                          color: 'error',
                          onClick: (event: Partial<ICompany>) => {
                              setSelectedActionsRowId(event.id as string);
                              handleSetDialogRemoveCompany();
                          },
                      },
                  ]
                : undefined,
        [handleSetDialogRemoveCompany, isAdmin, isDashboard]
    );

    useEffect(() => {
        dispatch(setTitle('Companies List'));
    }, [dispatch, isDashboard]);

    return (
        <>
            <title>{isDashboard ? 'Dashboard - ' : ''}My Companies</title>
            <div
                className={clsx('font-inter w-full h-full flex flex-col gap-4', {
                    [PUBLIC_PADDING_CLASSNAME]: !isDashboard,
                })}
            >
                {!isDashboard ? <CompanyHeader withSelect={false} className="mb-10" /> : null}
                <div className="bg-white rounded">
                    <Filters
                        searchInput={search ?? ''}
                        onSearchChange={(e) => setSearch(e.target.value)}
                        onSearchClear={() => setSearch('')}
                        searchPlaceholder="Search for company..."
                    />
                    <LoadingContainer isLoading={isLoading}>
                        <div className="sm:px-4 sm:pb-4 rounded">
                            <Table
                                headers={COMPANY_OVERVIEW_TABLE_HEADERS_DASHBOARD_USER}
                                data={userCompanies?.results ?? []}
                                renderRow={renderRow}
                                emptyListTxt="There are currently no companies to list"
                                rowsPerPage={PAGE_SIZE}
                                className="min-w-[1100px]"
                                onSortClick={handleSortClick}
                                page={Number(filters.page)}
                                onPageChange={handlePageChange}
                                sort={sort}
                                count={userCompanies?.count || 0}
                                rowOptions={{ data: rowOptions }}
                                isLoading={isFetching}
                                error={managementUserCompaniesError || publicUserCompaniesError}
                                getHref={(row) => `/app${isDashboard ? '/dashboard' : ''}/companies?id=${row.id}`}
                                fillMissingRows
                            />
                        </div>
                    </LoadingContainer>
                </div>
                <CancelDeleteDialog
                    open={!!isDialogOpen}
                    title={dialogInfo?.title}
                    primary={{
                        label: dialogInfo?.primary.label || '',
                        onClick: dialogInfo?.primary.onClick || (() => {}),
                        loading: isDeleteLoading,
                    }}
                    secondary={{
                        label: dialogInfo?.secondary.label || '',
                        onClick: handleDialogClose,
                    }}
                />
            </div>
        </>
    );
};

export { CompaniesList };
